<template>
  <div class="notification-container">
    <div class="header">
      <span class="title">{{ $t('notification.notification') }}</span>
      <p v-if="!isShowAll" class="view-all" @click="onClickPage('notification')">{{ $t('notification.viewAll') }}</p>
    </div>
    <ul class="tabs-component-tabs">
      <li :class="{ 'is-active': activeTab === 'TAB_ALL' }" class="tabs-component-tab" @click="activeTab = 'TAB_ALL'">{{ $t('app.all') }}</li>
      <li :class="{ 'is-active': activeTab === 'TAB_UNREAD' }" class="tabs-component-tab" @click="activeTab = 'TAB_UNREAD'">{{ $t('notification.unread') }}</li>
    </ul>
    <div class="notification-list" v-infinite-scroll="onLoadMore" infinite-scroll-disabled="showLoadMore" infinite-scroll-distance="10">
      <button class="gradient-button w-full mb-2" style="height: 64px" @click="onClickBenefits">
        <i class="fal fa-heart" style="color: #ffffff;"></i>
        {{ $t('notification.benefits.label') }}
      </button>
      <div
        :key="notification.id"
        class="notification-item"
        :class="{'isError': isNotificationError(notification.label_type)}"
        v-for="notification in filteredNotifications"
        @click="onClickNotificationItem($event, notification)"
      >
        <div class="d-flex">
          <div class="content">
            <h5 class="title" v-html="renderTitle(notification.title)"></h5>
            <p class="time">{{ renderDate(notification.created_at) }}</p>
          </div>
          <button v-if="!notification.is_readed" class="btn actions" @click="onMarkAsRead($event, notification.id)">
            <div class="tick"/>
          </button>
        </div>
      </div>
      <div class="no-data" v-if="filteredNotifications.length === 0 && !notificationsLoading">
        {{ $t('notification.noAnnouncement') }}
      </div>
      <notification-skeleton v-if="filteredNotifications.length === 0 && notificationsLoading"/>
      <div v-if="filteredNotifications.length !== 0 && notificationsLoading" class="loading-more">
        <i class="fad fa-spinner-third fa-spin"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import NumericUtils from '@/utils/numeric';
import DateTimeUtils from '@/utils/datetime';
import { EVENT_BUS, NOTIFICATION_TYPE } from '@/app-constant';
import AccountProxy from '@/store/proxies/account-proxy';
import NotificationSkeleton from './skeleton/index';

const TABS = {
  TAB_ALL: 'TAB_ALL',
  TAB_UNREAD: 'TAB_UNREAD',
};

export default {
  name: 'AppNotification',
  components: { NotificationSkeleton },
  props: {
    isShowAll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: TABS.TAB_ALL,
    };
  },
  computed: {
    ...mapState([
      'notifications',
      'unreadNotifications',
      'notificationsLoading',
    ]),
    filteredNotifications() {
      return this.activeTab === TABS.TAB_UNREAD
        ? this.unreadNotifications.data
        : this.notifications.data;
    },
    showLoadMore() {
      const paging = this.activeTab === TABS.TAB_UNREAD
        ? this.unreadNotifications.page_info
        : this.notifications.page_info;
      return !(paging.total > (paging.current_page * paging.current_limit));
    },
  },
  methods: {
    ...mapActions(['loadMoreNotifications', 'markAsRead', 'setState']),
    isNotificationError(type) {
      return type === 'error';
    },
    renderTitle(titleString) {
      const match = titleString.match(/(\d+)( hoá đơn)/);
      if (match) {
        const number = match[1];
        const numberAndInvoice = `${NumericUtils.formatAmount(number)}${match[2]}`;
        const parts = titleString.split(match[0]);

        return `${parts[0]}<strong>${numberAndInvoice}</strong>${parts[1]}.`;
      }
      return `${titleString}.`;
    },
    renderDate(dateString) {
      if (!dateString) return '';
      const date = DateTimeUtils.parseDate(dateString, 'YYYY-MM-DDTHH:mm:ss.SSSZ');
      return `${date.hour()}:${date.minute()} ${date.date()}/${date.month() + 1}/${date.year()}`;
    },
    onMarkAsRead($event, id) {
      $event.stopPropagation();
      this.markAsRead([id]);
    },
    onLoadMore() {
      const paging = this.activeTab === TABS.TAB_UNREAD
        ? this.unreadNotifications.page_info
        : this.notifications.page_info;
      this.loadMoreNotifications({ page: paging.current_page + 1, is_readed: this.activeTab === TABS.TAB_UNREAD ? false : undefined });
    },
    onClickPage(page) {
      const query = {
        ...this.$route.query,
      };
      delete query.page;
      delete query.origin;
      delete query.page_size;
      delete query.search_text;
      delete query.invoice_type;
      delete query.invoice_outgoing;
      delete query.exclude_invoice_types;
      this.$router.push({ name: page, query }).catch(() => {});
    },
    onClickNotificationItem($event, notificationItem) {
      this.$root.$emit('bv::hide::popover', 'btn-notification');
      if (this.isNotificationError(notificationItem.label_type)) {
        this.$modal.show('tax-account-connect-modal', { model: { isUpdatePassword: false } });
        if (!notificationItem.is_readed) this.markAsRead([notificationItem.id]);
      } else {
        switch (notificationItem.type) {
          case NOTIFICATION_TYPE.SYNC_PROCESS_0_SUCCESS:
          case NOTIFICATION_TYPE.SYNC_PROCESS_1_SUCCESS:
          case NOTIFICATION_TYPE.SYNC_PROCESS_2_SUCCESS:
            this.$modal.show('detailed-notification-modal', {
              params: {
                start_date: DateTimeUtils.formatDate(notificationItem.content.start_date, 'YYYY-MM-DD'),
                end_date: DateTimeUtils.formatDate(notificationItem.content.end_date, 'YYYY-MM-DD'),
              },
              notification: notificationItem,
            });
            break;
          case NOTIFICATION_TYPE.ACCOUNT_CQT_ERROR:
            new AccountProxy().checkValidTaxAccount().then((res) => {
              if (res) {
                localStorage.setItem('startDateSynchronized', res.start_date_synchronized);
                this.setState({
                  checkValid: res,
                  startDateSynchronized: res.start_date_synchronized,
                });
                if (!res.expired) {
                  sessionStorage.setItem('isConnectTaxAccount', '1');
                  this.$modal.show('setting-modal', { activeTab: 'COMPANY_TAX_ACCOUNT' });
                } else {
                  this.$modal.show('tax-account-connect-modal', { model: { isUpdatePassword: true } });
                }
              }
            }, () => {
              this.$modal.show('tax-account-connect-modal', { model: { isUpdatePassword: false } });
            });
            break;
          case NOTIFICATION_TYPE.RELEASE_INVOICE_SUCCESS:
            this.$modal.show('detailed-notification-modal', {
              params: {
                id: notificationItem.ref_id,
              },
              notification: notificationItem,
            });
            break;
          case NOTIFICATION_TYPE.SYNC_TBPH_SUCCESS:
            $event.preventDefault();
            $event.stopPropagation();
            if (!notificationItem.is_readed) this.markAsRead([notificationItem.id]);
            this.$bus.emit(EVENT_BUS.DRIVE_CREATE_INVOICE);
            break;
          default:
            break;
        }
      }
    },
    onClickBenefits($event) {
      $event.stopPropagation();
      this.$root.$emit('bv::hide::popover', 'btn-notification');
      this.$modal.show('benefits-modal', {});
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/app-notification/index.vue